import React, { useState, useRef, useCallback } from 'react';
import { registerDriver, checkVehicleNumber, checkInnExists } from 'service/DriverRegistrationService';
import { login, getUserInfo } from 'service/AuthService';
import tokenDecode from 'utils/decode';
import { useDispatch } from 'react-redux';
import { setUser } from 'store/slices/UserSlice';
import { useNavigate } from 'react-router-dom';
import Notification from 'utils/Notifications';
import styles from './DriverRegistrationForm.module.scss';

const STEPS = ['Личные данные', 'Транспорт', 'Документы'];

const DOC_SLOTS = [
  { name: 'passport_front',      icon: '🪪', group: 'Паспорт',                    side: 'Лицевая сторона',  hint: 'Страница с фото и данными' },
  { name: 'passport_back',       icon: '🪪', group: 'Паспорт',                    side: 'Обратная сторона', hint: 'Страница с пропиской' },
  { name: 'license_front',       icon: '🚗', group: 'Водительское удостоверение',  side: 'Лицевая сторона',  hint: 'Сторона с фото и фамилией' },
  { name: 'license_back',        icon: '🚗', group: 'Водительское удостоверение',  side: 'Обратная сторона', hint: 'Обратная сторона прав' },
  { name: 'tech_passport_front', icon: '📋', group: 'Технический паспорт',         side: 'Лицевая сторона',  hint: 'Страница с данными автомобиля' },
  { name: 'tech_passport_back',  icon: '📋', group: 'Технический паспорт',         side: 'Обратная сторона', hint: 'Обратная сторона тех. паспорта' },
];

const DocPhotoCard = ({ slot, value, preview, error, onFile, onBack, onNext, docStep, totalSlots }) => {
  const inputRef = useRef(null);
  return (
    <div className={styles.docCard}>
      <div className={styles.docCounter}>{docStep + 1} из {totalSlots}</div>
      <div className={styles.docCardIcon}>{slot.icon}</div>
      <div className={styles.docCardGroup}>{slot.group}</div>
      <div className={styles.docCardSide}>{slot.side}</div>
      <div className={styles.docCardHint}>{slot.hint}</div>
      <div
        className={`${styles.docCardZone} ${value ? styles.docCardZoneDone : ''} ${error ? styles.docCardZoneError : ''}`}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.pdf"
          className={styles.uploadInput}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const f = e.target.files[0];
            e.target.value = ''; // allow re-selecting same file
            if (f) onFile(slot.name, f);
          }}
        />
        {value ? (
          <div className={styles.docCardDone}>
            {preview && <img src={preview} alt="preview" className={styles.docCardPreview} />}
            <div className={styles.docCardDoneText}>✅ Загружено!</div>
            <div className={styles.docCardChangeText}>Нажмите для замены</div>
          </div>
        ) : (
          <div className={styles.docCardEmpty}>
            <span className={styles.docCardCamIcon}>📸</span>
            <div className={styles.docCardCamText}>Нажмите, чтобы выбрать фото</div>
            <div className={styles.docCardCamHint}>jpg, png, pdf, webp</div>
          </div>
        )}
      </div>
      {error && <div className={styles.errorText}>{error}</div>}
      <div className={styles.docCardNav}>
        {onBack && <button className={styles.btnSecondary} onClick={onBack}>← Назад</button>}
        {value && onNext && (
          <button className={styles.docCardNextBtn} onClick={onNext}>Далее →</button>
        )}
      </div>
    </div>
  );
};

const FileUploadField = ({ label, name, accept, onChange, value, required, error }) => {
  const inputRef = useRef(null);
  const isImage = accept && accept.includes('image');
  const [preview, setPreview] = useState(null);

  const handleChange = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;
    if (isImage) {
      const url = URL.createObjectURL(file);
      setPreview(url);
    }
    onChange(name, file);
  };

  return (
    <div className={styles.field}>
      <label className={styles.label}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      <div
        className={`${styles.uploadZone} ${value ? styles.uploadZoneFilled : ''} ${error ? styles.uploadZoneError : ''}`}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept || '*'}
          className={styles.uploadInput}
          onChange={handleChange}
          onClick={(e) => e.stopPropagation()}
        />
        {isImage && preview ? (
          <>
            <img src={preview} alt="preview" className={styles.uploadPreview} />
            <div className={styles.uploadTextSmall}>Нажмите, чтобы заменить</div>
          </>
        ) : (
          <>
            <span className={styles.uploadIcon}>{isImage ? '📷' : '📄'}</span>
            {value ? (
              <div className={styles.uploadFileName}>✓ {value.name}</div>
            ) : (
              <>
                <div className={styles.uploadText}>Нажмите для выбора файла</div>
                <div className={styles.uploadTextSmall}>jpg, png, pdf, webp</div>
              </>
            )}
          </>
        )}
      </div>
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

const DriverRegistrationForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  // Personal
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [phone, setPhone] = useState('');
  const [inn, setInn] = useState('');

  // Vehicle
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [photoVehicle, setPhotoVehicle] = useState(null);

  // Documents
  const [passportFront, setPassportFront] = useState(null);
  const [passportBack, setPassportBack] = useState(null);
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  const [techPassportFront, setTechPassportFront] = useState(null);
  const [techPassportBack, setTechPassportBack] = useState(null);

  const [errors, setErrors] = useState({});

  // Doc wizard
  const [docStep, setDocStep] = useState(0);
  const [docPreviews, setDocPreviews] = useState({});

  const setFile = useCallback((name, file) => {
    const map = {
      photo_vehicle: setPhotoVehicle,
      passport_front: setPassportFront,
      passport_back: setPassportBack,
      license_front: setLicenseFront,
      license_back: setLicenseBack,
      tech_passport_front: setTechPassportFront,
      tech_passport_back: setTechPassportBack,
    };
    if (map[name]) map[name](file);
    setErrors((prev) => ({ ...prev, [name]: null }));
  }, []);

  const handleDocFile = useCallback((name, file) => {
    setFile(name, file);
    if (file && file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setDocPreviews(prev => ({ ...prev, [name]: url }));
    }
    const idx = DOC_SLOTS.findIndex(s => s.name === name);
    if (idx >= 0) {
      setTimeout(() => setDocStep(Math.min(idx + 1, DOC_SLOTS.length)), 900);
    }
  }, [setFile]);

  const validateStep0 = () => {
    const errs = {};
    if (!lastName.trim()) errs.last_name = 'Обязательное поле';
    if (!firstName.trim()) errs.first_name = 'Обязательное поле';
    if (!phone.trim() || phone.length < 9) errs.phone = 'Введите корректный номер';
    if (!inn.trim() || inn.length < 10) errs.inn = 'ИНН должен содержать минимум 10 цифр';
    return errs;
  };

  const validateStep1 = () => {
    const errs = {};
    if (!vehicleNumber.trim()) errs.vehicle_number = 'Обязательное поле';
    if (!photoVehicle) errs.photo_vehicle = 'Прикрепите фото транспорта';
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!passportFront) errs.passport_front = 'Обязательное';
    if (!passportBack) errs.passport_back = 'Обязательное';
    if (!licenseFront) errs.license_front = 'Обязательное';
    if (!licenseBack) errs.license_back = 'Обязательное';
    if (!techPassportFront) errs.tech_passport_front = 'Обязательное';
    if (!techPassportBack) errs.tech_passport_back = 'Обязательное';
    return errs;
  };

  const [checkingInn, setCheckingInn] = useState(false);
  const [checkingVehicle, setCheckingVehicle] = useState(false);

  const handleNext = async () => {
    let errs = {};
    if (step === 0) errs = validateStep0();
    if (step === 1) errs = validateStep1();
    if (Object.keys(errs).length) {
      setErrors(errs);
      setNotify({ isOpen: true, message: 'Заполните все обязательные поля', type: 'warning' });
      return;
    }

    // On step 0: check INN uniqueness before proceeding
    if (step === 0 && inn.trim()) {
      setCheckingInn(true);
      try {
        const res = await checkInnExists(inn.trim());
        if (res.data.exists) {
          const msg = 'Учётная запись с таким ИНН уже существует. Войдите в систему.';
          setErrors({ inn: msg });
          setNotify({ isOpen: true, message: msg, type: 'error' });
          return;
        }
      } catch (e) {
        // network error — allow to proceed
      } finally {
        setCheckingInn(false);
      }
    }

    // On step 1: check vehicle number uniqueness before proceeding
    if (step === 1 && vehicleNumber.trim()) {
      setCheckingVehicle(true);
      try {
        const res = await checkVehicleNumber(vehicleNumber.trim());
        if (!res.data.available) {
          setErrors({ vehicle_number: res.data.detail });
          setNotify({ isOpen: true, message: res.data.detail, type: 'error' });
          setCheckingVehicle(false);
          return;
        }
      } catch (e) {
        // network error — allow to proceed
      } finally {
        setCheckingVehicle(false);
      }
    }

    setErrors({});
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    const errs = validateStep2();
    if (Object.keys(errs).length) {
      setErrors(errs);
      setNotify({ isOpen: true, message: 'Загрузите все документы', type: 'warning' });
      return;
    }

    const fd = new FormData();
    fd.append('first_name', firstName.trim());
    fd.append('last_name', lastName.trim());
    fd.append('middle_name', middleName.trim());
    fd.append('phone', phone.trim());
    fd.append('inn', inn.trim());
    fd.append('vehicle_number', vehicleNumber.trim());
    fd.append('vehicle_model', vehicleModel.trim());
    if (photoVehicle) fd.append('photo_vehicle', photoVehicle);
    if (passportFront) fd.append('passport_front', passportFront);
    if (passportBack) fd.append('passport_back', passportBack);
    if (licenseFront) fd.append('license_front', licenseFront);
    if (licenseBack) fd.append('license_back', licenseBack);
    if (techPassportFront) fd.append('tech_passport_front', techPassportFront);
    if (techPassportBack) fd.append('tech_passport_back', techPassportBack);

    setLoading(true);
    try {
      const res = await registerDriver(fd);
      const { access, user_id } = res.data;

      // Save token
      localStorage.setItem('token', access);

      // Load user info
      const userRes = await getUserInfo(user_id);
      const userData = { ...userRes.data, token: access };
      dispatch(setUser(userData));
      localStorage.setItem('user', JSON.stringify(userData));

      setSuccess(true);
      setTimeout(() => {
        navigate('/report-card/');
        window.location.reload();
      }, 2500);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Ошибка при регистрации';
      setNotify({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fileValues = {
    passport_front: passportFront,
    passport_back: passportBack,
    license_front: licenseFront,
    license_back: licenseBack,
    tech_passport_front: techPassportFront,
    tech_passport_back: techPassportBack,
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div>
              <h2 className={styles.title}>Регистрация водителя</h2>
              <p className={styles.subtitle}>Заполните все данные для подачи заявки</p>
            </div>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Закрыть">✕</button>
          </div>

          {!success && (
            <div className={styles.steps}>
              {STEPS.map((label, i) => (
                <div
                  key={i}
                  className={`${styles.step} ${i === step ? styles.stepActive : ''} ${i < step ? styles.stepDone : ''}`}
                >
                  <div className={styles.stepDot}>{i < step ? '✓' : i + 1}</div>
                  <div className={styles.stepLabel}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className={styles.body}>
          {success ? (
            <div className={styles.successBlock}>
              <div className={styles.successIcon}>🎉</div>
              <div className={styles.successTitle}>Заявка отправлена!</div>
              <p className={styles.successText}>
                Ваша заявка на регистрацию передана в администрацию. Вы будете перенаправлены
                в личный кабинет, где сможете следить за статусом.
              </p>
            </div>
          ) : step === 0 ? (
            <>
              <p className={styles.sectionTitle}>Личные данные</p>
              <div className={styles.grid3}>
                <div className={styles.field}>
                  <label className={styles.label}>Фамилия <span className={styles.required}>*</span></label>
                  <input className={`${styles.input} ${errors.last_name ? styles.inputError : ''}`}
                    placeholder="Бакытов" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  {errors.last_name && <span className={styles.errorText}>{errors.last_name}</span>}
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Имя <span className={styles.required}>*</span></label>
                  <input className={`${styles.input} ${errors.first_name ? styles.inputError : ''}`}
                    placeholder="Бакыт" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  {errors.first_name && <span className={styles.errorText}>{errors.first_name}</span>}
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Отчество</label>
                  <input className={`${styles.input} ${errors.middle_name ? styles.inputError : ''}`}
                    placeholder="Бакытович" value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
                  {errors.middle_name && <span className={styles.errorText}>{errors.middle_name}</span>}
                </div>
              </div>
              <div className={styles.grid2} style={{ marginTop: 14 }}>
                <div className={styles.field}>
                  <label className={styles.label}>Телефон <span className={styles.required}>*</span></label>
                  <input className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                    placeholder="+996 700 000 000" type="tel" value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} />
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>ИНН <span className={styles.required}>*</span></label>
                  <input className={`${styles.input} ${errors.inn ? styles.inputError : ''}`}
                    placeholder="Минимум 10 цифр" type="text" inputMode="numeric" value={inn}
                    onChange={(e) => setInn(e.target.value.replace(/\D/g, ''))} maxLength={14} />
                  {errors.inn && <span className={styles.errorText}>{errors.inn}</span>}
                </div>
              </div>
              <div className={styles.notice} style={{ marginTop: 16 }}>
                ИНН будет использован как логин и пароль для входа в систему.
              </div>
            </>
          ) : step === 1 ? (
            <>
              <p className={styles.sectionTitle}>Данные транспортного средства</p>
              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label className={styles.label}>Гос. номер <span className={styles.required}>*</span></label>
                  <input className={`${styles.input} ${errors.vehicle_number ? styles.inputError : ''}`}
                    placeholder="01 KG 000" value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())} />
                  {errors.vehicle_number && <span className={styles.errorText}>{errors.vehicle_number}</span>}
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Марка/модель</label>
                  <input className={styles.input} placeholder="КАМАЗ 5511"
                    value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)} />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <FileUploadField
                  label="Фото транспорта"
                  name="photo_vehicle"
                  accept="image/*"
                  onChange={setFile}
                  value={photoVehicle}
                  required
                  error={errors.photo_vehicle}
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.docProgressBar}>
                {DOC_SLOTS.map((slot, i) => (
                  <div
                    key={i}
                    className={[
                      styles.docProgressDot,
                      i < docStep ? styles.docProgressDotDone : '',
                      i === docStep ? styles.docProgressDotActive : '',
                    ].join(' ')}
                  />
                ))}
              </div>

              {docStep < DOC_SLOTS.length ? (
                <DocPhotoCard
                  key={DOC_SLOTS[docStep].name}
                  slot={DOC_SLOTS[docStep]}
                  value={fileValues[DOC_SLOTS[docStep].name]}
                  preview={docPreviews[DOC_SLOTS[docStep].name]}
                  error={errors[DOC_SLOTS[docStep].name]}
                  onFile={handleDocFile}
                  onBack={docStep > 0 ? () => setDocStep(s => s - 1) : null}
                  onNext={fileValues[DOC_SLOTS[docStep].name] ? () => setDocStep(s => Math.min(s + 1, DOC_SLOTS.length)) : null}
                  docStep={docStep}
                  totalSlots={DOC_SLOTS.length}
                />
              ) : (
                <div className={styles.docAllDone}>
                  <div className={styles.docAllDoneIcon}>🎉</div>
                  <div className={styles.docAllDoneTitle}>Все документы загружены!</div>
                  <div className={styles.docAllDoneList}>
                    {DOC_SLOTS.map((slot, i) => (
                      <div key={i} className={styles.docAllDoneItem} onClick={() => setDocStep(i)}>
                        <span>{slot.icon} {slot.group} — {slot.side}</span>
                        <span>{fileValues[slot.name] ? '✅' : '❌ (нажмите)'}</span>
                      </div>
                    ))}
                  </div>
                  <div className={styles.docAllDoneHint}>Проверьте и нажмите «Отправить заявку» ↓</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className={styles.footer}>
            {step > 0 && (
              <button className={styles.btnSecondary} onClick={() => setStep((s) => s - 1)} disabled={loading}>
                Назад
              </button>
            )}
            <button className={styles.btnSecondary} onClick={onClose} disabled={loading}>
              Отмена
            </button>
            {step < 2 ? (
              <button className={styles.btnPrimary} onClick={handleNext} disabled={loading || checkingInn || checkingVehicle}>
                {(checkingInn || checkingVehicle) ? 'Проверка...' : 'Далее →'}
              </button>
            ) : (
              <button className={styles.btnPrimary} onClick={handleSubmit} disabled={loading}>
                {loading ? 'Отправка...' : 'Отправить заявку'}
              </button>
            )}
          </div>
        )}
      </div>
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
};

export default DriverRegistrationForm;
